// src/pages/VerifyAccount.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "../services/api";

export default function VerifyAccount() {
  const navigate = useNavigate();
  const location = useLocation();

  // prefill email from state or query (?email=)
  const initialEmail = useMemo(() => {
    const fromState = location.state?.email || "";
    if (fromState) return fromState;
    const qs = new URLSearchParams(location.search);
    return qs.get("email") || "";
  }, [location.state, location.search]);

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds

  useEffect(() => {
    let t;
    if (cooldown > 0) {
      t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    }
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const res = await api.post("/api/auth/verify", { email: email.trim(), code: code.trim() });
      setMessage(typeof res.data === "string" ? res.data : "Verified! Redirecting…");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const msg = err.response?.data || "Verification failed.";
      setMessage(typeof msg === "string" ? msg : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    setResendMessage("");
    try {
      const res = await api.post("/api/auth/resend-code", { email: email.trim() });
      setResendMessage(typeof res.data === "string" ? res.data : "Code sent. Check your inbox.");
      setCooldown(30); // 30s cooldown
    } catch (err) {
      const msg = err.response?.data || "Could not resend code.";
      setResendMessage(typeof msg === "string" ? msg : "Could not resend code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="page">
      <div className="mx-auto max-w-lg">
        <section className="card p-6 md:p-8">
          <header className="mb-6 text-center">
            <h1 className="font-display text-3xl md:text-4xl">
              Verify your <span className="bg-gradient-to-r from-brand-500 to-glow bg-clip-text text-transparent">email</span>
            </h1>
            <p className="mt-2 text-slate-300">
              We’ve sent a verification code to your inbox.
            </p>
          </header>

          {message && (
            <div
              className={`mb-4 rounded-xl border p-3 text-sm ${
                /success|verified|redirecting/i.test(message)
                  ? "border-green-400/20 bg-green-500/10 text-green-200"
                  : "border-amber-400/20 bg-amber-500/10 text-amber-200"
              }`}
              aria-live="polite"
            >
              {message}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <div className="field">
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="field">
              <label htmlFor="code" className="label">Verification code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                // Allow 4–8 chars; strip spaces; uppercase just in case
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\s/g, "").toUpperCase().slice(0, 8))
                }
                className="input tracking-widest"
                placeholder="e.g. 123456"
                required
              />
              <p className="help">Enter the code from the email we sent.</p>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading || !email || !code}
            >
              {loading ? "Verifying…" : "Verify"}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-2 text-sm text-slate-300">
            <button
              onClick={handleResend}
              className="underline disabled:no-underline disabled:opacity-60"
              disabled={resending || cooldown > 0 || !email}
              type="button"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification code"}
            </button>
            {resendMessage && <p className="text-slate-200">{resendMessage}</p>}
          </div>

          <div className="mt-6 text-center text-sm text-slate-400">
            Already verified? <Link to="/login" className="text-slate-200 underline">Log in</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
